import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface SheetContextValue<T = unknown> {
  /** Whether the sheet is open */
  open: boolean;
  /** Function to set open state, optionally passing data */
  setOpen: (open: boolean, data?: T) => void;
  /** Close the sheet */
  close: () => void;
  /** Data passed via setOpen */
  data: T | null;
  /** Whether clicking the overlay should close the sheet */
  closeOnOverlayClick: boolean;
}

const SheetContext = createContext<SheetContextValue | null>(null);

export function useSheet(): SheetContextValue {
  const context = useContext(SheetContext);
  if (!context) {
    throw new Error("useSheet must be used within a SheetProvider");
  }
  return context;
}

/**
 * Body scroll lock, shared across every open sheet.
 *
 * Each provider used to capture and restore the body overflow on its own,
 * which broke in two ways: it saved the *computed* value ("visible") and wrote
 * it back as an inline style the page never had, and when sheets nested, the
 * inner one captured the "hidden" set by the outer and restored "hidden",
 * leaving the page permanently unscrollable.
 *
 * A module-level count fixes both: the first lock saves the real inline value
 * and the last unlock puts exactly that back.
 */
let scrollLockCount = 0;
let savedInlineOverflow = "";

const lockBodyScroll = () => {
  if (scrollLockCount === 0) {
    savedInlineOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  scrollLockCount += 1;
};

const unlockBodyScroll = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedInlineOverflow;
  }
};

/**
 * Stack of sheets currently listening for Escape, innermost last.
 *
 * Every provider used to attach its own document-level keydown handler with no
 * notion of depth, so one Escape closed every open sheet at once. Only the top
 * of this stack acts.
 */
const escapeStack: symbol[] = [];

interface SheetProviderProps {
  children: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Default open state (uncontrolled) */
  defaultOpen?: boolean;
  /** Close the sheet when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Close the sheet when the overlay is clicked. Default true. */
  closeOnOverlayClick?: boolean;
}

/**
 * SheetProvider - Root component that manages sheet state
 */
export function SheetProvider({
  children,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  closeOnEscape = true,
  closeOnOverlayClick = true,
}: SheetProviderProps) {
  const [_open, _setOpen] = useState(defaultOpen);
  const [data, setData] = useState<unknown>(null);

  // Use controlled state if provided, otherwise use internal state
  const open = openProp ?? _open;

  const setOpen = useCallback(
    (value: boolean, newData?: unknown) => {
      if (value) {
        setData(newData ?? null);
      } else {
        setData(null);
      }

      // Always advance internal state and always notify. Branching on
      // onOpenChange meant a caller passing it *without* `open` could never
      // open the sheet: internal state was never written and openProp was
      // undefined. Controlled callers are unaffected -- openProp still wins
      // above -- and uncontrolled callers have no onOpenChange to call.
      _setOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Handle Escape key to close -- topmost open sheet only
  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const id = Symbol("sheet");
    escapeStack.push(id);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Only the innermost sheet reacts, so Escape does not collapse a whole
      // stack of nested sheets at once.
      if (escapeStack[escapeStack.length - 1] !== id) return;
      event.preventDefault();
      close();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const index = escapeStack.indexOf(id);
      if (index !== -1) escapeStack.splice(index, 1);
    };
  }, [open, closeOnEscape, close]);

  // Prevent body scroll when sheet is open
  useEffect(() => {
    if (!open) return;

    lockBodyScroll();
    return unlockBodyScroll;
  }, [open]);

  const value: SheetContextValue = useMemo(
    () => ({
      open,
      setOpen,
      close,
      data,
      closeOnOverlayClick,
    }),
    [open, setOpen, close, data, closeOnOverlayClick],
  );

  return <SheetContext value={value}>{children}</SheetContext>;
}
